// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import DefaultFields from "../../../components/DefaultFields/DefaultFields";

// HELPERS
import {createClassName} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./Kyc.fulfillment";
import EditableField from "../../../components/EditableField/EditableField";
import TextView from "../../../../../components/TextView/TextView";
import OptionsList from "../../../components/OptionsList/OptionsList";
import OriginOfFunds from "../../../components/KYC/OriginOfFunds";
import SizeOfDeposits from "../../../components/KYC/SizeOfDeposits";
import FrequencyOfDeposits from "../../../components/KYC/FrequencyOfDeposits";
import OriginOfDeposits from "../../../components/KYC/OriginOfDeposits";
import PurposeOfDeposits from "../../../components/KYC/PurposeOfDeposits";
import PepQuestions from "../../../components/PepQuestions/PepQuestions";


export default class Kyc extends React.PureComponent
{
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired
	};

	static companyExclusive = false;
	static privateExclusive = true;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState)
	{
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields)
		{
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}


	render()
	{
		const {props, state} = this;
		const {title} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled
		});

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields} />

				<div className="Cob-Fields">
					<h2>{title}</h2>

					<p style={{marginTop: "0", fontWeight: "600"}}>Sysselsättning senaste 24 månaderna</p>

					<EditableField
						name="customer_occupation_last24m"
						Component={TextView}
						disableEnterKey={true}
						label={`${250 - state.fields.customer_occupation_last24m.length} tecken kvar`}
						value={state.fields.customer_occupation_last24m}
						maxLength={250}
						valid={
							state.fields.customer_occupation_last24m.length > 0 &&
							state.fields.customer_occupation_last24m.length <= 250 ? true : null
						}
						resize="vertical"
						onChange={this._onFieldChange}
						onBlur={this._onFieldBlur}
						style={{height: "100px", marginBottom: "10px"}}
					/>

					<p style={{fontWeight: "600"}}>Vad arbetar du huvudsakligen med?</p>

					<EditableField
						name="customer_occupation"
						Component={TextView}
						disableEnterKey={true}
						label={`${250 - state.fields.customer_occupation.length} tecken kvar`}
						value={state.fields.customer_occupation}
						maxLength={250}
						valid={
							state.fields.customer_occupation.length > 0 &&
							state.fields.customer_occupation.length <= 250 ? true : null
						}
						resize="vertical"
						onChange={this._onFieldChange}
						onBlur={this._onFieldBlur}
						style={{height: "100px", marginBottom: "30px"}}
					/>

					<OptionsList
						type="radio"
						orientation="horizontal"
						name="checkbox_aml_own_money"
						description="Äger du själv pengarna som kommer att placeras på kontot?"
						options={state.fields.checkbox_aml_own_money.options}
						onChange={this._onOptionsListChange}
					/>

					{state.fields.checkbox_aml_own_money.options[0].checked && (
						<div className="Notice warning" style={{marginTop: "20px"}}>
							Som kund hos oss måste du äga de pengar och/eller de värdepapper du vill spara eller investera i.
							Eftersom du har kryssat "Nej" kan du inte slutföra uppdateringen av kundkännedomsuppgifter här,
							ta istället kontakt med din rådgivare.
						</div>
					)}

					<OriginOfFunds onUpdate={(fulfilled) => this.setState({
						fields: {...this.state.fields, originOfFundsFulfilled: fulfilled}
					})} />

					<SizeOfDeposits onUpdate={(fulfilled) => this.setState({
						fields: {...this.state.fields, sizeOfDepositsFulfilled: fulfilled}
					})} />

					<FrequencyOfDeposits onUpdate={(fulfilled) => this.setState({
						fields: {...this.state.fields, frequencyOfDepositsFulfilled: fulfilled}
					})} />

					<OriginOfDeposits onUpdate={(fulfilled) => this.setState({
						fields: {...this.state.fields, originOfDepositsFulfilled: fulfilled}
					})} />

					<PurposeOfDeposits onUpdate={(fulfilled) => this.setState({
						fields: {...this.state.fields, purposeOfDepositsFulfilled: fulfilled}
					})} />

					<PepQuestions onUpdate={this._onPepChange} appliesToUbo={this.props.session.isCompany}/>
				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) =>
	{
		if (name === "customer_social_security_number")
		{
			return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
		}
		else if (name === "customer_email")
		{
			return Validator.validateEmail(value);
		}
		else
		{
			return value.length > 0;
		}
	};



	_onPepChange = (valid) => {
		CobHelpers.onViewFieldChange.call(this, "pep_fields_valid", valid);
	};

	_onFieldChange = (name, value) =>
	{
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) =>
	{
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onCheckboxChange = (name, checked) =>
	{
		this.setState({
			fields: {
				...this.state.fields,
				[name]: {...this.state.fields[name], checked}
			}
		});
	};

	_onOptionsListChange = ({type, name}, option, index) =>
	{
		const newOptions = [...this.state.fields[name].options].map((o, i) =>
		{
			if (i === index)
			{
				return option;
			}
			else
			{
				return {...o, checked: type === "radio" ? false : o.checked};
			}
		});

		this.setState({
			fields: {
				...this.state.fields,
				[name]: {
					options: newOptions
				}
			}
		}, () => fulfillmentCheck(this.props, this.state));
	};

	_onSelectPepChange = (option, index) => {
		this.setState({
			fields: {
				...this.state.fields,
				[option.name]: {
					...this.state.fields[option.name],
					index: index,
				},
				[option.name + "_value"]: option.label
			},
		}, () => fulfillmentCheck(this.props, this.state));
	};
}


// PRIVATE FUNCTIONS
function _getFields()
{
	return [
		"customer_occupation_last24m",
		"customer_occupation",
		"checkbox_aml_own_money"
	];
}

function _getInitialState()
{
	if (this.props.prevState)
	{
		return {...this.props.prevState};
	}

	return {
		validFields: {},
		fields: {
			customer_occupation_last24m: "",
			customer_occupation: "",
			checkbox_aml_own_money: {options: _createYesNoOptions()},

			pep_fields_valid: false,

			originOfFundsFulfilled: false,
			sizeOfDepositsFulfilled: false,
			frequencyOfDepositsFulfilled: false,
			originOfDepositsFulfilled: false,
			purposeOfDepositsFulfilled: false
		}
	};
}

function _createYesNoOptions()
{
	return [
		{label: "Nej", value: "no", checked: false},
		{label: "Ja", value: "yes", checked: false}
	];
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Fair/Kyc/Kyc.js